import { MarshalModelFactory, Model, Utilities } from '@socrates/common';

export class CachedRecord<T extends Model> {
	private _expiringAt: number;       // the time the page expires
	private _startHit: number;         // the start hit of the page
	private _pageLength: number;       // the desired length of the page
	private _pageResultsCount: number; // the actual length of the page (last page could be shorter)
	private _records: T[] = [];        // the records themselves

	// default expire at 10 minutes
	constructor(
		private modelFactory: MarshalModelFactory<T>,  // deep clone mechanism
		private expirationMilliseconds: number = 60 * 10 * 1000,
		initialRecords: T[] = null,
		startHit = 0,
		pageLength = 0
	) {
		if (initialRecords && initialRecords.length > 0) {
			this._records = [...initialRecords];
			this._pageResultsCount = initialRecords.length;
			this._startHit = startHit;
			this._pageLength = pageLength;
			this.resetExpiration();
		}
	}

	get expired(): boolean {
		return (new Date().getTime() - this._expiringAt) >= 0;
	}

	get pageRecords(): T[] {
		return this.expired ? [] : [...this._records.splice(0, this._pageResultsCount)];
	}

	/**
	 * Should only be called when server request is resolved
	 * records:    results of a page of records being pulled from server
	 * startHit:   the starting (0-based) index in the results set (20 - 30 of 250)
	 * pageLength: the length of the current page (not the page size)
	 */
	setPageRecords(records: T[], startHit: number, pageLength: number) {
		this._records = records;
		this._startHit = startHit;
		this._pageLength = pageLength;
		this._pageResultsCount = records.length;
		this.resetExpiration();
	}

	get numPageHits(): number {
		return this._pageResultsCount;
	}

	get originalRecords(): T[] {
		return this.expired ? [] : this._records;
	}

	get records(): T[] {
		return this.expired ? [] : [...this._records];
	}

	set records(records: T[]) {
		this._records = records;
		this._startHit = this._pageLength = this._pageResultsCount = 0;
		this.resetExpiration();
	}

	get startHit(): number {
		return this._startHit;
	}

	expireResults(): void {
		this._expiringAt = new Date('2018-01-01T23:00:00.000Z').getTime();
	}

	getRecord(recordId: string, originalRecord = false): T {
		// using loop so I can break once found
		let rekord: T = null;

		for (const rec of this._records) {
			if (rec.id === recordId) {
				if (!originalRecord) {
					rekord = this.modelFactory.clone(rec);
				} else {
					rekord = rec;
				}
				break;
			}
		}
		return rekord;
	}

	postRecord(record: T): T {
		// doesn't bump expiration
		let found = false;

		for (let li = 0; li < this._records.length; li++) {
			const rec: T = this._records[li];
			if (rec.id === record.id) {
				this._records[li] = record;
				found = true;
				break;
			}
		}
		if (!found) {
			this._records.push(record);
		}
		const copy: T = this.modelFactory.clone(record);
		return copy;
	}

	removeRecord(record: T): boolean {
		let found = false;

		for (let li = 0; li < this._records.length; li++) {
			const rec: T = this._records[li];
			if (rec.id === record.id) {
				if (this._pageLength > 0 && li >= this._pageLength) {
					// need to expire page since we no longer have a full page
					this.expireResults();
				}
				Utilities.removeItem<T>(this._records, rec);
				found = true;
				break;
			}
		}
		return found;
	}

	removeRecordById(id: string): boolean {
		let found = false;

		for (let li = 0; li < this._records.length; li++) {
			const rec: T = this._records[li];
			if (rec.id === id) {
				if (this._pageLength > 0 && li >= this._pageLength) {
					// need to expire page since we no longer have a full page
					this.expireResults();
				}
				Utilities.removeItem<T>(this._records, rec);
				found = true;
				break;
			}
		}
		return found;
	}

	resetExpiration(): void {
		this._expiringAt = new Date().getTime() + this.expirationMilliseconds;
	}
}
